import { X } from 'lucide-react'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/utils'
import { getCameraDetector, getZxingDetector, stripeScore, visibleFrame } from '@/services/scan/cameraDetector'

interface CameraScannerProps {
  /** Where the small strip opens (the button's box). */
  anchor: DOMRect
  onScan: (code: string) => void
  onClose: () => void
  /** Keep scanning after a read (selling); otherwise close after the first. */
  continuous?: boolean
}

type View = 'small' | 'large'

const SMALL = { w: 240, h: 84 }
const TICK_MS = 120
const SAME_CODE_MS = 1500
const BACK_TO_SMALL_TICKS = 25

const box = (view: View, anchor: DOMRect) => {
  const vw = window.innerWidth
  const vh = window.innerHeight
  if (view === 'large') {
    const w = Math.min(vw - 32, 560)
    const h = Math.min(vh - 96, Math.round(w * 0.6))
    return { left: (vw - w) / 2, top: Math.max(16, (vh - h) / 2), width: w, height: h }
  }
  const left = Math.min(Math.max(16, anchor.right - SMALL.w), vw - SMALL.w - 16)
  const below = anchor.bottom + 8
  const top = below + SMALL.h + 16 > vh ? Math.max(16, anchor.top - SMALL.h - 8) : below
  return { left, top, width: SMALL.w, height: SMALL.h }
}

/**
 * Camera scanning for any scan field: a small barcode-shaped live strip that grows into a large view as soon as
 * something code-like is in frame, reads QR or barcode by itself, flashes, shrinks back and hands the code over.
 */
export const CameraScanner = ({ anchor, onScan, onClose, continuous = false }: CameraScannerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [view, setView] = useState<View>('small')
  const [flash, setFlash] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const viewRef = useRef<View>('small')
  const onScanRef = useRef(onScan)
  onScanRef.current = onScan

  const show = (next: View) => {
    viewRef.current = next
    setView(next)
  }

  useEffect(() => {
    let stream: MediaStream | null = null
    let timer = 0
    let stopped = false
    let tick = 0
    let busy = false
    let quiet = 0
    let hits = 0
    let floor = Infinity
    let last = { code: '', at: 0 }
    const canvas = document.createElement('canvas')
    const frameCanvas = document.createElement('canvas')

    const loop = async () => {
      const video = videoRef.current
      if (stopped || !video) return
      if (video.readyState >= video.HAVE_CURRENT_DATA && !busy) {
        tick++
        const score = stripeScore(video, canvas)
        floor = Math.min(floor * 1.02, score)
        const codeLike = score > Math.max(16, floor * 1.8)
        hits = codeLike ? hits + 1 : 0
        quiet = codeLike ? 0 : quiet + 1
        if (viewRef.current === 'small' && hits >= 2) show('large')
        if (viewRef.current === 'large' && quiet >= BACK_TO_SMALL_TICKS) show('small')

        if (viewRef.current === 'large' || tick % 2 === 0) {
          busy = true
          try {
            const frame = visibleFrame(video, frameCanvas)
            // Large view: the built-in reader and ZXing take turns; ZXing often reads long CODE128 the other misses.
            const detector = viewRef.current === 'large' && tick % 2 ? await getZxingDetector() : await getCameraDetector()
            const [found] = frame ? await detector.detect(frame) : []
            const now = Date.now()
            // The same code counts again only after it has been out of view for a moment, not while it is held there.
            const repeat = found?.rawValue === last.code && now - last.at <= SAME_CODE_MS
            if (found?.rawValue) last = { code: found.rawValue, at: now }
            if (found?.rawValue && !stopped && !repeat) {
              navigator.vibrate?.(60)
              setFlash(true)
              window.setTimeout(() => setFlash(false), 350)
              show('small')
              hits = 0
              onScanRef.current(found.rawValue)
              if (!continuous) {
                stopped = true
                window.setTimeout(onClose, 350)
                return
              }
            }
          } catch {
            // A frame the decoder cannot read; the next one is tried.
          } finally {
            busy = false
          }
        }
      }
      timer = window.setTimeout(loop, TICK_MS)
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('No camera here. The camera needs a secure (https) address.')
    } else {
      navigator.mediaDevices
        .getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
        .then((s) => {
          stream = s
          const [track] = s.getVideoTracks()
          const caps = (track?.getCapabilities?.() ?? {}) as { focusMode?: string[] }
          if (caps.focusMode?.includes('continuous')) {
            track.applyConstraints({ advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet] }).catch(() => undefined)
          }
          if (import.meta.env.DEV) console.info('[camera]', track?.getSettings().width, '×', track?.getSettings().height)
          if (stopped || !videoRef.current) return s.getTracks().forEach((t) => t.stop())
          videoRef.current.srcObject = s
          videoRef.current.play().catch(() => undefined)
          getCameraDetector().catch(() => setError('The barcode reader could not load.'))
          loop()
        })
        .catch((e: DOMException) =>
          setError(
            e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError'
              ? 'No camera found on this device.'
              : e?.name === 'NotReadableError'
                ? 'The camera is in use by another app.'
                : 'Camera blocked. Allow camera access for this site.'
          )
        )
    }

    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => {
      stopped = true
      window.clearTimeout(timer)
      window.removeEventListener('keydown', onKey)
      stream?.getTracks().forEach((t) => t.stop())
    }
  }, [continuous, onClose])

  const rect = box(view, anchor)

  return createPortal(
    <>
      {view === 'large' && <div className="fixed inset-0 z-[60] bg-frame/60" onClick={() => show('small')} aria-hidden />}
      <div
        role="dialog"
        aria-label="Camera scanner"
        style={rect}
        className={cn(
          'fixed z-[61] overflow-hidden rounded-md border-2 bg-frame shadow-dialog transition-all duration-200 ease-out',
          flash ? 'border-success' : 'border-border'
        )}
      >
        {error ? (
          <p className="grid h-full place-items-center px-3 text-center text-xs text-white">{error}</p>
        ) : (
          <>
            <video ref={videoRef} muted playsInline className="size-full object-cover" />
            <span className="pointer-events-none absolute inset-x-3 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-error/80" />
            {view === 'large' && (
              <span className="pointer-events-none absolute inset-x-[12%] inset-y-[22%] rounded-sm border-2 border-white/70" />
            )}
          </>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close camera"
          className="absolute right-1.5 top-1.5 grid size-6 place-items-center rounded-full bg-frame/70 text-white hover:bg-frame"
        >
          <X className="size-3.5" />
        </button>
      </div>
    </>,
    document.body
  )
}
